import axios from 'axios';
import { config } from '../config';
import { ApiResponse } from '../types';

export interface GuestData {
  userId: string;
  token: string;
  refreshToken: string;
}

export interface GuestSessionResponse {
  session: any;
  player: any;
  token: string;
  refreshToken: string;
  guestUserId: string;
}

const GUEST_STORAGE_KEYS = {
  USER_ID: 'guestUserId',
  TOKEN: 'guestToken',
  REFRESH_TOKEN: 'guestRefreshToken',
} as const;

/**
 * Service for managing guest user lifecycle in the frontend
 */
class GuestService {
  /**
   * Decode JWT token to get payload
   */
  private decodeToken(token: string): any {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        return null;
      }
      const payload = JSON.parse(atob(tokenParts[1]!));
      return payload;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  /**
   * Check if a JWT token is expired
   */
  private isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) {
      return true; // Consider invalid tokens as expired
    }
    
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    return currentTime >= expirationTime;
  }

  /**
   * Check if a JWT token is close to expiration (within threshold)
   */
  private isTokenExpiringSoon(token: string, thresholdMinutes: number = 60): boolean {
    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) {
      return true; // Consider invalid tokens as expiring soon
    }
    
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    const thresholdMs = thresholdMinutes * 60 * 1000;
    
    return timeUntilExpiration < thresholdMs;
  }

  /**
   * Get guest data from localStorage
   */
  getGuestData(): GuestData | null {
    const userId = localStorage.getItem(GUEST_STORAGE_KEYS.USER_ID);
    const token = localStorage.getItem(GUEST_STORAGE_KEYS.TOKEN);
    const refreshToken = localStorage.getItem(GUEST_STORAGE_KEYS.REFRESH_TOKEN);

    if (!userId || !token || !refreshToken) {
      return null;
    }

    return { userId, token, refreshToken };
  }

  /**
   * Store guest data in localStorage (e.g. after redeem or guest session create).
   */
  saveGuestData(data: GuestData): void {
    localStorage.setItem(GUEST_STORAGE_KEYS.USER_ID, data.userId);
    localStorage.setItem(GUEST_STORAGE_KEYS.TOKEN, data.token);
    localStorage.setItem(GUEST_STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
  }

  /**
   * Clear guest data from localStorage
   */
  clearGuestData(): void {
    localStorage.removeItem(GUEST_STORAGE_KEYS.USER_ID);
    localStorage.removeItem(GUEST_STORAGE_KEYS.TOKEN);
    localStorage.removeItem(GUEST_STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Get or create a guest user
   * - Checks localStorage for existing guest data
   * - Validates token hasn't expired
   * - Creates new guest if needed
   */
  async getOrCreateGuest(): Promise<GuestData> {
    // Check for existing guest data
    const existingData = this.getGuestData();
    
    if (existingData) {
      // Check if token is still valid
      if (!this.isTokenExpired(existingData.token)) {
        return existingData;
      }
      
      // Token expired, try to refresh
      try {
        const refreshed = await this.refreshGuestToken(existingData.refreshToken);
        if (refreshed) {
          return refreshed;
        }
      } catch (error) {
        // Refresh failed, will create new guest below
        console.warn('Failed to refresh guest token, creating new guest:', error);
      }
    }

    // No valid guest data, create new guest session
    // This will be done via createGuestSession, so we return null here
    // The caller should call createGuestSession to get guest data
    throw new Error('No guest data available. Call createGuestSession to create a new guest.');
  }

  /**
   * Get or create guest user ID (backward compatibility)
   */
  async getOrCreateGuestId(): Promise<string> {
    try {
      const guestData = await this.getOrCreateGuest();
      return guestData.userId;
    } catch (error) {
      // If getOrCreateGuest fails, we need to create a session first
      // This method is for backward compatibility, but should be used with createGuestSession
      throw new Error('Guest ID not available. Create a guest session first.');
    }
  }

  /**
   * Check if a session belongs to a guest user
   */
  isGuestSession(_sessionId: string, createdByUserId: string): boolean {
    const guestData = this.getGuestData();
    if (!guestData) {
      return false;
    }
    return guestData.userId === createdByUserId;
  }

  /**
   * Refresh guest token
   */
  async refreshGuestToken(refreshToken: string): Promise<GuestData | null> {
    try {
      const response = await axios.post<ApiResponse<{ token: string; refreshToken: string }>>(
        `${config.apiBaseUrl}/api/auth/refresh`,
        { refreshToken }
      );

      if (!response.data.data) {
        throw new Error('Invalid refresh response');
      }

      const { token, refreshToken: newRefreshToken } = response.data.data;
      
      // Get existing userId or create new guest if refresh fails
      const existingData = this.getGuestData();
      const userId = existingData?.userId;
      
      if (!userId) {
        // No userId available, refresh token might be for a different user
        // Return null to indicate we need to create a new guest
        return null;
      }

      const guestData: GuestData = {
        userId,
        token,
        refreshToken: newRefreshToken,
      };

      this.saveGuestData(guestData);
      return guestData;
    } catch (error) {
      // Refresh failed, clear guest data
      this.clearGuestData();
      return null;
    }
  }

  /**
   * Check if guest token needs refresh (1 hour before expiration for 24-hour tokens)
   */
  shouldRefreshGuestToken(): boolean {
    const guestData = this.getGuestData();
    if (!guestData) {
      return false;
    }
    
    // Guest tokens expire in 24 hours, refresh 1 hour before (23 hours in)
    return this.isTokenExpiringSoon(guestData.token, 60); // 60 minutes = 1 hour
  }

  /**
   * Check if current user is a guest (based on stored guest data)
   */
  isGuest(): boolean {
    return !!this.getGuestData();
  }
}

// Create singleton instance
const guestService = new GuestService();

export default guestService;

